import { appRoutes } from "../../../lib/appRoutes";
import { useCreateLeadOneClick } from "../../../lib/hooks/api/leads/useCreateLeadOneClick";
import { EventData, EventNames, useTracking } from "../../../lib/tracking";
import { SharedSaveProps } from "../../entities/utils";
import Button from "../../ui/button/Button";
import { CreateLeadFormOutput } from "../create_lead/utils";
import { useViewLeadsDrawer } from "../viewing/ViewLeadsDrawerProvider";
import { useSaveLeadDrawer } from "./SaveLeadDrawerProvider";

type Props = SharedSaveProps<CreateLeadFormOutput> & {
  trackingData: EventData;
  savedLeadIds: string[];
  hasBuyerLeads: boolean;
  buttonProps?: {
    fullWidth?: boolean;
    disabled?: boolean;
  };
};

/**
 * Button that creates a lead from an entity in one click, or takes the user to the lead if it already exists.
 *
 * If the entity is saved to multiple leads, the user is taken to a drawer to select which lead to view.
 * Lead is created with default values passed as props.
 *
 * If `hasBuyerLeads` is true, clicking "Create Lead" opens the SaveLeadDrawer instead of
 * creating a duplicate lead.
 */
export default function CreateOrViewLeadButton({
  entityType,
  entityId,
  trackingData,
  savedLeadIds,
  defaultValues,
  hasBuyerLeads,
  buttonProps,
}: Props) {
  const { logEvent } = useTracking();
  const { openViewLeadsDrawer } = useViewLeadsDrawer();
  const { openLeadDrawer } = useSaveLeadDrawer();
  const fullWidth = buttonProps?.fullWidth ?? false;
  const disabled = buttonProps?.disabled ?? false;

  const { createLeadFromDefaultValues, isLoading: isCreatingLead } = useCreateLeadOneClick({
    defaultValues,
    trackingData: { ...trackingData, "Data type": entityType?.toUpperCase() },
    onCreateLead: (leadId: string) => {
      logEvent(EventNames.saveDataClicked, {
        "Context source": "Save button",
        "Lead ID": leadId,
        "Data type": entityType,
        ...trackingData,
      });
    },
  });

  const isSaved = savedLeadIds.length > 0;

  if (!isSaved) {
    return (
      <Button
        onClick={(e) => {
          e.stopPropagation();
          if (hasBuyerLeads) {
            openLeadDrawer({ entityType, entityId, defaultValues, buyerMatchOnly: true });
          } else {
            createLeadFromDefaultValues(e);
          }
        }}
        loading={isCreatingLead}
        fullWidth={fullWidth}
        disabled={disabled}
      >
        Create Lead
      </Button>
    );
  }

  // Single lead: render as a link for better accessibility
  if (savedLeadIds.length === 1) {
    return (
      <Button
        fullWidth={fullWidth}
        as="a"
        onClickCapture={(e) => e.stopPropagation()}
        {...appRoutes.leadDetail(savedLeadIds[0])}
        buttonType="secondary"
      >
        View Lead
      </Button>
    );
  }

  // Multiple leads: open drawer to select
  return (
    <Button
      fullWidth={fullWidth}
      buttonType="secondary"
      onClick={(e) => {
        e.stopPropagation();
        openViewLeadsDrawer({
          savedLeadIds,
          entityType,
        });
      }}
    >
      View Lead
    </Button>
  );
}
